/**
 * Employee Web — `/me/requests` inbox index route (EW-11).
 *
 * Phase 4 Plan 04-04 Task 2. Renders the full `<MyRequestsList>` (status
 * FilterChip enabled; full list, no `limit`). See `_auth.me.requests.tsx`
 * for the pathless parent.
 */
import type { JSX } from "react";
import { z } from "zod";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "@linkhr/i18n";
import { Button, PageHeader } from "@linkhr/design-system";
import { Link } from "@tanstack/react-router";
import { MyOtherRequestsSection, MyRequestsList } from "@linkhr/employee-self-service";

/** `?tab=` deep-link from the dashboard Requests card (e.g. ?tab=pending). */
const requestsSearchSchema = z.object({
  tab: z.enum(["all", "pending", "approved", "rejected"]).optional(),
});

export const Route = createFileRoute("/_auth/me/requests/")({
  component: RequestsInboxRoute,
  validateSearch: requestsSearchSchema,
});

function RequestsInboxRoute(): JSX.Element {
  const { t } = useTranslation("requests");
  const { tab } = Route.useSearch();
  return (
    <section className="flex flex-col gap-lg">
      <PageHeader
        title={t("page.my_requests", { defaultValue: "My Requests" })}
        actions={
          <Button asChild variant="primary" className="min-h-11">
            <Link to="/me/requests/new">
              {t("page.new_request", { defaultValue: "New Request" })}
            </Link>
          </Button>
        }
      />
      <MyRequestsList initialTab={tab} />
      {/* Miscellaneous requests list from their own endpoint — the unified
          feed only carries the chain-routed subset. */}
      <MyOtherRequestsSection />
    </section>
  );
}
